import axios from 'axios';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import qs from 'qs';
import get from 'lodash/get';
import classNames from 'classnames';
import { makeCancelable } from 'general/js/helpers';
import gtm from 'general/js/gtm';
import { postTypes, mediaTypes } from 'components/posts';
import { BlockPlaceholder } from 'components/block-placeholder';
import { BlockSpinner } from 'components/block-spinner';
import { LivePreMessage, bemClasses } from 'components/live-pre-message';
import LiveTextFilter from './live-text-filter';
import LiveTextListing from './live-text-listing';
import ScoringLoader, { endpointNames } from '../../../general/js/loaders/scoring-loader';

class LiveTextLayout extends React.Component {
    constructor(...args) {
        super(...args);
        this.data = null;
        this.timeoutId = null;
        this.state = {
            batches: [],
            nextBatchId: null,
            type: null,
            isFetching: false,
            showSpinner: true
        };

        this.cancelableGetData = null;
    }

    componentDidMount() {
        ScoringLoader.subscribe(endpointNames.LIVE_SCANNER);
        this.getData();
        this.getDataAfterTimeout();
    }

    componentWillUnmount() {
        ScoringLoader.unsubscribe(endpointNames.LIVE_SCANNER);
        clearTimeout(this.timeoutId);
        this.cancelGetData();
    }

    onLoadMore = () => {
        this.getData(true);
        gtm.push('Load More', 'Click', window.location.href);
    }

    getTopBatchId() {
        const topBatch = this.state.batches[0];

        return (topBatch && topBatch.id) || null;
    }

    getUpdatedBatches(newBatches) {
        const { batches } = this.state;

        for (let i = newBatches.length - 1; i >= 0; i--) {
            const newBatch = newBatches[i];
            const foundBatch = batches.find((batch) =>
                batch.id === newBatch.id);

            if (foundBatch) {
                if (foundBatch.lastUpdated !== newBatch.lastUpdated) {
                    foundBatch.posts = newBatch.posts;
                    foundBatch.lastUpdated = newBatch.lastUpdated;
                }
            } else {
                batches.unshift(newBatch);
            }
        }

        return batches;
    }

    getDataAfterTimeout() {
        const updateInSeconds = this.props.options.updateInSeconds || 20;

        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            this.setState({
                showSpinner: false,
            }, this.getData);
        }, updateInSeconds * 1000);
    }

    getData(isLoadMore) {
        const { context } = this.props.options;
        const { nextBatchId, type } = this.state;
        const qsProps = { context };
        let url = this.props.options.endpointUrl;

        if (isLoadMore) {
            url += '/' + nextBatchId;
        }

        if (type) {
            qsProps.type = type;
        }

        url += '?' + qs.stringify(qsProps);

        this.cancelGetData();
        this.cancelableGetData = makeCancelable(axios.get(url));
        this.cancelableGetData
            .promise
            .then((response) => {
                if (!response.data || !Array.isArray(response.data.batches)) {
                    return;
                }

                let batches = this.state.batches;
                let newNextBatchId = nextBatchId;

                if (isLoadMore) {
                    batches.push(...response.data.batches);
                    newNextBatchId = response.data.nextBatch;
                } else {
                    const topBatchId = this.getTopBatchId();
                    const hasTopBatch = response.data.batches.some((batch) =>
                        batch.id === topBatchId);

                    if (!hasTopBatch) {
                        batches = response.data.batches;
                        newNextBatchId = response.data.nextBatch;
                    } else {
                        batches = this.getUpdatedBatches(response.data.batches);
                    }
                }

                this.setState({
                    batches,
                    nextBatchId: newNextBatchId,
                    isFetching: false,
                });
                this.getDataAfterTimeout();
            })
            .catch((error) => {
                if (!error.isCanceled) {
                    this.setState({ isFetching: false, showSpinner: true });
                    this.getDataAfterTimeout();
                }
            });

        this.setState({ isFetching: true });
    }

    cancelGetData() {
        if (this.cancelableGetData) {
            this.cancelableGetData.cancel();
        }
    }

    filterByVideo = () => {
        this.setState({
            batches: [],
            nextBatchId: null,
            type: this.state.type ? null : mediaTypes.VIDEO_PLAYER,
        }, this.getData);

        gtm.push('Filter', 'Click', `Filter > ${window.location.href}`);
    }

    getPosts() {
        const { batches } = this.state;
        const posts = [];

        batches.forEach((batch) => {
            if (Array.isArray(batch.posts)) {
                posts.push(...batch.posts);
            }
        });

        return posts;
    }

    injectBannerCards() {
        const { banner } = this.props.options;
        const posts = this.getPosts();
        const bannerInterval = banner.interval || 10;

        if (!posts.length) return [];

        const postsWithBanners = [];
        let injectCount = 0;

        for (let i = 0; i < posts.length; i++) {
            postsWithBanners.push(posts[i]);

            if (i !== 0 && (i + 1) % bannerInterval === 0) {
                postsWithBanners.push({
                    id: `banner-${injectCount}`,
                    type: postTypes.BANNER_SPOT,
                    card: {
                        endpointUrl: banner.endpointUrl
                    }
                });
            }

            injectCount++;
        }

        return postsWithBanners;
    }

    render() {
        const { liveScanner } = this.props;
        const { context } = this.props.options;
        const { isFetching, showSpinner, nextBatchId } = this.state;
        const config = get(liveScanner, 'liveComponents.liveText');
        const isContextRight = (context === 'Live' || context === 'WOLive');

        if (config === undefined) {
            return (
                <BlockPlaceholder minHeight={300}>
                    <BlockSpinner />
                </BlockPlaceholder>
            );
        }

        if (isContextRight && !config.show) {
            const messageModel = {
                title: config.title,
                body: config.subtitle,
                textColor: config.textColor
            };
            return <LivePreMessage model={messageModel} bemList={[bemClasses.DARK]} />;
        }

        const posts = this.injectBannerCards();
        const loadMoreCss = classNames({
            'is-loading': isFetching
        });

        return (
            <div className="live-text">
                <LiveTextFilter
                    isFiltered={this.state.type !== null}
                    filterByVideo={this.filterByVideo}
                />
                {isFetching && showSpinner && <BlockSpinner hasOverlay />}
                <LiveTextListing posts={posts} options={this.props.options}/>
                {
                    nextBatchId &&
                    <div className={`listing-load-more spinner ${loadMoreCss}`}>
                        <button
                            className="listing-load-more__button button button--primary"
                            type="button"
                            onClick={this.onLoadMore}>
                            load more
                        </button>
                    </div>
                }
            </div>
        );
    }
}

LiveTextLayout.propTypes = {
    posts: PropTypes.array,
    holdingMessage: PropTypes.object,
    banner: PropTypes.shape({
        endpointUrl: PropTypes.string,
        interval: PropTypes.number
    }),
    filterByVideo: PropTypes.func
};

const mapStateToProps = (state) => ({
    liveScanner: state.scoring[endpointNames.LIVE_SCANNER],
});

const LiveTextLayoutContainer = connect(
    mapStateToProps,
    null
)(LiveTextLayout);

export default LiveTextLayoutContainer;
